import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { HealthConcernsSection } from "@/features/patient/clinical-history/components/HealthConcernsView";
import { useHealthConcerns } from "@/features/patient/clinical-history/utils/adaptClinicalHistory";
import {
  createHealthConcern,
  updateHealthConcern,
  type ConditionStatus,
  type HealthConcernInput,
} from "@/features/patient/records/api/dataPipelineApi";
import { invalidateRecords, useSetConditionStatus } from "@/portals/patient/api/patientQueries";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { useSearchFocusParam } from "@/shared/hooks/useSearchFocus";

export function HealthConcernsPage() {
  const { concerns, patientConcerns, isLoading } = useHealthConcerns();
  const client = useQueryClient();
  const { activeDependent } = useFamily();
  const profileId = activeDependent?.id;
  const focus = useSearchFocusParam();

  // Saved, then the shared records refetch so every page shows it at once.
  const saveConcern = useCallback(
    async (input: Partial<HealthConcernInput>, id?: string) => {
      if (id) await updateHealthConcern(id, input, profileId);
      else await createHealthConcern(input as HealthConcernInput, profileId);
      // Records only (clinical notes included, same prefix): a patient's own
      // concern never touches medicines or the review queue, which the shared
      // refresh also reloaded (connection plan step 4).
      void invalidateRecords(client, profileId);
    },
    [profileId, client],
  );
  // A provider's concern: the patient's own mark beside the hospital's record.
  // It moves to its new group at once; the records reload in the background.
  const { mutateAsync: markStatus } = useSetConditionStatus(profileId);
  const setStatus = useCallback(async (id: string, status: ConditionStatus) => {
    await markStatus({ id, status });
  }, [markStatus]);

  return (
    <HealthConcernsSection
      concerns={concerns}
      patientConcerns={patientConcerns}
      onSaveConcern={saveConcern}
      onSetStatus={setStatus}
      // The patient only: never while viewing a family member's profile.
      canEditStatus={!activeDependent}
      isLoading={isLoading}
      focus={focus}
    />
  );
}
